import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import UnstyledLink from 'src/components/shared/unstyledLink/unstyledLink';
import JobConfigPageService from 'src/services/jobConfigPageService';
import { setCurrentPage, setTotalPages } from 'src/store/jobConfigPageSlice/jobConfigPageSlice';
import { type JobConfigTableDataRow } from 'src/store/jobConfigPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

import ApplicableSitesPopover from '../applicableSitesPopover/applicableSitesPopover';

// **** Types **** //

type TOnSortArgs = {
  sortColumn: keyof JobConfigTableDataRow;
  sortDirection: TableSortDirection;
};

// **** Components **** //

/**
 * Default component
 */
const JobTable = () => {
  const dispatch = useDispatch();
  const { tableData, jobConfigTableFilter, searchField, currentPage, perPage } = useAppSelector(
    (state) => state.jobConfigPage,
  );

  // Local state
  const [sortColumn, setSortColumn] = useState<keyof JobConfigTableDataRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');

  // "onSort" function
  const onSort = useCallback(({ sortColumn, sortDirection }: TOnSortArgs) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  // Filter, sort, and paginate during render so row numbering stays in sync with the current page
  const { paginatedData: finalTableData, totalPages } = useMemo(() => {
    const filteredTableData = JobConfigPageService.applyJobConfigTableFilterUtil(tableData, jobConfigTableFilter);
    return filterSortAndPaginateTableData(
      filteredTableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      [],
      perPage,
    );
  }, [tableData, jobConfigTableFilter, sortColumn, sortDirection, searchField, currentPage, perPage]);

  // Keep the store's total page count in sync with the derived data
  useEffect(() => {
    dispatch(setTotalPages(totalPages));
  }, [totalPages]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, perPage]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="job-config__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell>Job #</TableCell>
          <TableCell sortColumn="name">Job Name</TableCell>
          <TableCell sortColumn="region">Region</TableCell>
          <TableCell sortColumn="jobType">Type</TableCell>
          <TableCell sortColumn="siteType">Site Type</TableCell>
          <TableCell>Applicable Site(s)</TableCell>
          <TableCell sortColumn="active">Is Active?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {finalTableData.map((row, index) => (
          <UncontrolledExpandableRow key={row.id} data-testid="job-config-table__row">
            <TableCell>{perPage * (currentPage - 1) + index + 1}</TableCell>
            <TableCell>{row.name || ''}</TableCell>
            <TableCell>{row.region || ''}</TableCell>
            <TableCell>{row.jobType || ''}</TableCell>
            <TableCell>{row.siteType || ''}</TableCell>
            <TableCell>{row.id && <ApplicableSitesPopover jobId={row.id} />}</TableCell>
            <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.description || ''}</TableCell>
            <TableCell>
              <UnstyledLink to={`/createNewJob/${row.id}`}>
                <Icon tokens={editTokens}></Icon>
              </UnstyledLink>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

// **** Export **** //

export default JobTable;
